'use client';

import { useEffect, useState } from "react";
import { Skeleton } from "@/components/shadcn/skeleton";

type InstagramPost = {
  id: string;
  caption?: string;
  media_type: "IMAGE" | "VIDEO" | "CAROUSEL_ALBUM";
  // Instagram video records may only expose thumbnail_url.
  media_url?: string;
  thumbnail_url?: string;
  permalink: string;
};

type InstagramResponse = {
  data?: InstagramPost[];
  error?: string;
};

type InstagramPostSectionProps = {
  heading?: string;
  enable_instagram_feed?: boolean;
  add_background?: boolean;
  add_bottom_border?: boolean;
};

export default function InstagramPostSection({
  heading,
  enable_instagram_feed = true,
  add_background = false,
  add_bottom_border = false,
}: InstagramPostSectionProps) {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enable_instagram_feed) {
      setLoading(false);
      return;
    }

    const fetchPosts = async () => {
      try {
        const response = await fetch('/api/instagram-posts');
        const data = (await response.json()) as InstagramResponse;
        if (!response.ok || data.error) {
          throw new Error(data.error || 'Failed to fetch Instagram posts');
        }
        setPosts(data.data ?? []);
      } catch (err) {
        console.error('Failed to fetch Instagram posts:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [enable_instagram_feed]);

  if (!enable_instagram_feed) {
    return null;
  }

  const content = (
    <>
      {heading && (
        <h2 className="text-3xl md:text-4xl font-bold text-primary-700 mb-8">{heading}</h2>
      )}
      
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="w-full aspect-square rounded-2xl" />
          ))}
        </div>
      )}

      {error && !loading && (
        <div className="text-gray-500 text-center py-8">
          Failed to load Instagram posts
        </div>
      )}

      {!loading && !error && posts.length === 0 && (
        <div className="text-gray-500 text-center py-8">
          No Instagram posts available
        </div>
      )}

      {!loading && !error && posts.length > 0 && (
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${add_bottom_border ? "pb-8 border-b border-gray-200" : ""}`}>
          {posts.slice(0, 3).map((post) => {
            const imageUrl = post.media_type === "VIDEO"
              ? post.thumbnail_url ?? post.media_url
              : post.media_url ?? post.thumbnail_url;

            // Ignore malformed records instead of rendering an empty image.
            if (!imageUrl || !post.permalink) return null;

            return (
              <a
                key={post.id}
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={post.caption ?? "Instagram post"}
                className="group relative aspect-square overflow-hidden rounded-3xl hover:opacity-90 transition-opacity shadow-md"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={imageUrl}
                  alt={post.caption ?? "Instagram post"}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </a>
            );
          })}
        </div>
      )}
    </>
  );

  if (add_background) {
    return (
      <section className="w-screen relative left-1/2 right-1/2 -mx-[50vw] bg-gradient-to-b from-[rgba(237,237,249,0)] to-[#DEE5E8] pt-12 pb-12">
        <div
          className="absolute left-0 bottom-0 w-[300px] h-[300px] bg-no-repeat bg-left-bottom bg-contain pointer-events-none"
          style={{
            backgroundImage: "url(/images/left-corner-supergrafis.png)",
          }}
        />
        <div className="container max-w-7xl mx-auto items-start gap-8 flex">
          <div className="w-80 shrink-0" />
          <div className="flex-1 flex flex-col">
            {content}
          </div>
        </div>
      </section>
    );
  }

  return <div className="flex flex-col">{content}</div>;
}
